import { unzipSync } from "fflate";
import type { Address, Hex } from "viem";
import type { ChainWriter, ExtensionEntry } from "@woodpecker/chain";
import {
  checkPackage,
  hash16,
  packageUnzipFilter,
  sha256Hex,
  type ExtensionManifest,
  type PackageCheck,
} from "@woodpecker/protocol";

/** An entry of the ExtensionDirectory as the Console shows it, with the manifest its url serves (D70, D72). */
export interface ExtensionView {
  id: string; // handle/name
  owner: Address;
  handle: string;
  name: string;
  url: string;
  hash: Hex;
  hash16: string;
  updatedAt: number;
  active: boolean;
  mine: boolean;
  manifest: ExtensionManifest | null;
  /** The manifest at the url hashes to the value the chain holds (D79). */
  verified: boolean;
  problem?: string;
}

/** The package read in the browser (D71): the zip's files and the checks of `checkPackage`. */
export function readPackageZip(bytes: Uint8Array, opts: { handle?: string } = {}): PackageCheck {
  // the entries inflate only within the limits, as at the host: the sizes come from the zip's directory
  const bound = packageUnzipFilter();
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(bytes, { filter: bound.filter });
  } catch (e) {
    return {
      manifest: null,
      hash: null,
      files: [],
      problems: [{ code: "manifest", detail: `not a zip: ${e instanceof Error ? e.message : String(e)}` }],
    };
  }
  const check = checkPackage(entries, opts);
  if (bound.dropped.length > 0)
    check.problems = [
      ...bound.dropped,
      ...check.problems.filter((p) => !(p.code === "missing" && bound.dropped.some((d) => d.path === p.path))),
    ];
  return check;
}

/** The manifest a version's url serves and its sha256: what the chain's hash is checked against. */
export async function fetchManifest(
  url: string,
  timeoutMs = 8000,
): Promise<{ manifest: ExtensionManifest | null; hash: Hex | null; problem?: string }> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(`${url}manifest.json`, { signal: ctl.signal, cache: "no-store" });
    if (!res.ok) return { manifest: null, hash: null, problem: `manifest.json → ${res.status}` };
    const bytes = new Uint8Array(await res.arrayBuffer());
    const hash = sha256Hex(bytes);
    try {
      return { manifest: JSON.parse(new TextDecoder().decode(bytes)) as ExtensionManifest, hash };
    } catch {
      return { manifest: null, hash, problem: "manifest.json is not JSON" };
    }
  } catch (e) {
    return { manifest: null, hash: null, problem: e instanceof Error ? e.message : String(e) };
  } finally {
    clearTimeout(timer);
  }
}

async function viewOf(
  chain: ChainWriter,
  e: ExtensionEntry,
  handles: Map<string, string>,
  wallet: Address | null,
): Promise<ExtensionView> {
  let handle = handles.get(e.owner.toLowerCase());
  if (!handle) {
    const h = await chain.dirHandleOf(e.owner);
    handle = h.exists ? `${h.name}@${h.addr4}` : `?@${e.owner.slice(2, 6).toLowerCase()}`;
    handles.set(e.owner.toLowerCase(), handle);
  }
  const m = await fetchManifest(e.url);
  return {
    id: `${handle}/${e.name}`,
    owner: e.owner,
    handle,
    name: e.name,
    url: e.url,
    hash: e.hash,
    hash16: hash16(e.hash),
    updatedAt: e.updatedAt,
    active: e.active,
    mine: !!wallet && e.owner.toLowerCase() === wallet.toLowerCase(),
    manifest: m.manifest,
    verified: !!m.hash && m.hash.toLowerCase() === e.hash.toLowerCase() && !!m.manifest,
    problem: m.problem,
  };
}

/**
 * The extensions (D69, D70, D72): this wallet's own entries, active or not (My extensions, with Manage), and the
 * other owners' active entries (the Directory, with Install); each manifest fetched from its url and checked against
 * the hash on chain. A stack without an ExtensionDirectory gives two empty lists and the error.
 */
export async function loadExtensions(
  chain: ChainWriter,
  wallet: Address | null,
): Promise<{ mine: ExtensionView[]; directory: ExtensionView[]; error?: string }> {
  const handles = new Map<string, string>();
  try {
    const n = await chain.extCount();
    const entries: ExtensionEntry[] = [];
    for (let i = 0; i < n; i++) entries.push(await chain.extEntryAt(i));
    const views = await Promise.all(entries.map((e) => viewOf(chain, e, handles, wallet)));
    return {
      mine: views.filter((v) => v.mine),
      directory: views.filter((v) => !v.mine && v.active),
    };
  } catch (e) {
    return { mine: [], directory: [], error: e instanceof Error ? e.message : String(e) };
  }
}

/** One extension by id, for its management page: null when the chain does not hold it. */
export async function loadExtension(
  chain: ChainWriter,
  id: string,
  wallet: Address | null,
): Promise<ExtensionView | null> {
  const [handle, name] = id.split("/");
  if (!handle || !name) return null;
  const all = await loadExtensions(chain, wallet);
  return [...all.mine, ...all.directory].find((v) => v.id === id) ?? null;
}
